import { intersectRect, invertMatrix, normalizeRect, subtractRect, transformRectBoundsPrecise, type Matrix, type Point, type Rect } from './geometry';

export type SelectionCombineMode = 'replace' | 'add' | 'subtract' | 'intersect';

export interface SelectionState {
  readonly regions: readonly Rect[];
  readonly inverted: boolean;
}

export const EMPTY_SELECTION: SelectionState = { regions: [], inverted: false };

export function hasSelection(selection: SelectionState): boolean {
  return selection.inverted || selection.regions.length > 0;
}

export function pointIsSelected(selection: SelectionState, point: Point): boolean {
  if (!hasSelection(selection)) return true;
  const inside = selection.regions.some(
    (rect) => point.x >= rect.x && point.y >= rect.y && point.x < rect.x + rect.width && point.y < rect.y + rect.height,
  );
  return selection.inverted ? !inside : inside;
}

/**
 * Returns an ordered, non-overlapping union with adjacent cells coalesced.
 *
 * Implemented as a top-to-bottom sweep. Band boundaries are the distinct
 * horizontal edges of the input, so within one band every region either covers
 * a column interval completely or misses it entirely; merging the active
 * regions' x-intervals therefore yields exactly the covered spans without
 * having to test every column cell against every region. Selection sources
 * feed this scan-line runs — one rectangle per covered pixel row — so the cost
 * has to stay near-linear in the number of runs rather than quadratic.
 */
export function canonicalizeRegions(input: readonly Rect[]): Rect[] {
  const regions = input
    .filter((rect) => [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite))
    .map(normalizeRect)
    .filter((rect) => rect.width > 0 && rect.height > 0);
  if (regions.length < 2) return regions.map((rect) => ({ ...rect }));
  const ys = [...new Set(regions.flatMap((rect) => [rect.y, rect.y + rect.height]))].sort((a, b) => a - b);
  const byTop = [...regions].sort((a, b) => a.y - b.y);
  const completed: Rect[] = [];
  let active = new Map<string, Rect>();
  let live: Rect[] = [];
  let cursor = 0;
  for (let yIndex = 0; yIndex < ys.length - 1; yIndex += 1) {
    const y = ys[yIndex];
    const bottom = ys[yIndex + 1];
    if (y === undefined || bottom === undefined) continue;
    while (cursor < byTop.length && (byTop[cursor]?.y ?? Infinity) <= y) {
      const entering = byTop[cursor];
      if (entering) live.push(entering);
      cursor += 1;
    }
    if (live.some((rect) => rect.y + rect.height <= y)) live = live.filter((rect) => rect.y + rect.height > y);
    const spans: Rect[] = [];
    if (live.length > 0) {
      const intervals = live.map((rect) => [rect.x, rect.x + rect.width] as const).sort((a, b) => a[0] - b[0]);
      let start = intervals[0]?.[0] ?? 0;
      let end = intervals[0]?.[1] ?? 0;
      for (let index = 1; index < intervals.length; index += 1) {
        const interval = intervals[index];
        if (!interval) continue;
        if (interval[0] <= end) end = Math.max(end, interval[1]);
        else {
          spans.push({ x: start, y, width: end - start, height: bottom - y });
          start = interval[0];
          end = interval[1];
        }
      }
      spans.push({ x: start, y, width: end - start, height: bottom - y });
    }
    const next = new Map<string, Rect>();
    for (const span of spans) {
      const key = `${span.x}:${span.width}`;
      const previous = active.get(key);
      if (previous && previous.y + previous.height === span.y) {
        previous.height += span.height;
        next.set(key, previous);
      } else next.set(key, span);
    }
    for (const [key, rect] of active) if (!next.has(key)) completed.push(rect);
    active = next;
  }
  completed.push(...active.values());
  return completed.sort((a, b) => a.y - b.y || a.x - b.x || a.height - b.height || a.width - b.width);
}

/** Ceiling on pixels rasterized while projecting a rotated/skewed layer selection. */
export const MAX_PROJECTED_SELECTION_PIXELS = 64_000_000;

/**
 * Projects layer-space selection runs into document space.
 *
 * Selection sources produce one rectangle per covered pixel row in *layer*
 * space. When the layer transform is axis-aligned each run's transformed
 * bounds are exact, so that path is kept. Under rotation or skew the
 * axis-aligned bounds of a 1px-tall run are far larger than the run itself —
 * a 100px run at 45 degrees becomes a ~71x71 box — which silently inflates the
 * selection into a solid blob. Those transforms are instead rasterized in
 * document space by inverse-mapping each candidate pixel back into the layer.
 *
 * Throws rather than truncating when the work exceeds the pixel ceiling, so
 * callers surface an honest failure instead of losing coverage silently.
 */
export function projectRegionsToDocument(
  regions: readonly Rect[],
  size: { readonly width: number; readonly height: number },
  layerToDocument: Matrix,
  options: { readonly maxPixels?: number } = {},
): Rect[] {
  const canonical = canonicalizeRegions(regions);
  if (canonical.length === 0) return [];
  const [, b, c] = layerToDocument;
  if (b === 0 && c === 0) return canonicalizeRegions(canonical.map((region) => transformRectBoundsPrecise(layerToDocument, region)));

  const maxPixels = options.maxPixels ?? MAX_PROJECTED_SELECTION_PIXELS;
  const layerWidth = Math.max(0, Math.floor(size.width));
  const layerHeight = Math.max(0, Math.floor(size.height));
  if (layerWidth <= 0 || layerHeight <= 0) return [];

  // Work over the covered area, not the whole layer. Quick Selection projects
  // on every pointer sample, so allocating a full-layer bitmap per call cost
  // tens of milliseconds and megabytes for a small brush blob.
  let coverLeft = Number.POSITIVE_INFINITY;
  let coverTop = Number.POSITIVE_INFINITY;
  let coverRight = Number.NEGATIVE_INFINITY;
  let coverBottom = Number.NEGATIVE_INFINITY;
  for (const region of canonical) {
    coverLeft = Math.min(coverLeft, Math.floor(region.x));
    coverTop = Math.min(coverTop, Math.floor(region.y));
    coverRight = Math.max(coverRight, Math.ceil(region.x + region.width));
    coverBottom = Math.max(coverBottom, Math.ceil(region.y + region.height));
  }
  coverLeft = Math.max(0, coverLeft);
  coverTop = Math.max(0, coverTop);
  coverRight = Math.min(layerWidth, coverRight);
  coverBottom = Math.min(layerHeight, coverBottom);
  const width = coverRight - coverLeft;
  const height = coverBottom - coverTop;
  if (width <= 0 || height <= 0) return [];
  if (width * height > maxPixels) throw new Error('The source layer is too large to project a rotated selection.');

  const inverse = invertMatrix(layerToDocument);
  if (!inverse) throw new Error('The layer transform cannot be inverted.');

  const bounds = transformRectBoundsPrecise(layerToDocument, { x: coverLeft, y: coverTop, width, height });
  const left = Math.floor(bounds.x);
  const top = Math.floor(bounds.y);
  const right = Math.ceil(bounds.x + bounds.width);
  const bottom = Math.ceil(bounds.y + bounds.height);
  if (right <= left || bottom <= top) return [];
  if ((right - left) * (bottom - top) > maxPixels) throw new Error('The projected selection area is too large to rasterize.');

  const coverage = new Uint8Array(width * height);
  for (const region of canonical) {
    const x0 = Math.max(coverLeft, Math.floor(region.x)) - coverLeft;
    const y0 = Math.max(coverTop, Math.floor(region.y)) - coverTop;
    const x1 = Math.min(coverRight, Math.ceil(region.x + region.width)) - coverLeft;
    const y1 = Math.min(coverBottom, Math.ceil(region.y + region.height)) - coverTop;
    for (let y = y0; y < y1; y += 1) if (x1 > x0) coverage.fill(1, y * width + x0, y * width + x1);
  }

  const projected: Rect[] = [];
  // Only the linear part varies per pixel step, so walk each row incrementally.
  for (let documentY = top; documentY < bottom; documentY += 1) {
    let sourceX = inverse[0] * (left + 0.5) + inverse[2] * (documentY + 0.5) + inverse[4];
    let sourceY = inverse[1] * (left + 0.5) + inverse[3] * (documentY + 0.5) + inverse[5];
    let start: number | null = null;
    for (let documentX = left; documentX <= right; documentX += 1) {
      let inside = false;
      if (documentX < right) {
        const layerX = Math.floor(sourceX) - coverLeft;
        const layerY = Math.floor(sourceY) - coverTop;
        inside = layerX >= 0 && layerY >= 0 && layerX < width && layerY < height && coverage[layerY * width + layerX] === 1;
      }
      if (inside && start === null) start = documentX;
      if (!inside && start !== null) {
        projected.push({ x: start, y: documentY, width: documentX - start, height: 1 });
        start = null;
      }
      sourceX += inverse[0];
      sourceY += inverse[1];
    }
  }
  return canonicalizeRegions(projected);
}

function subtractRegions(sources: readonly Rect[], cutters: readonly Rect[]): Rect[] {
  let result = canonicalizeRegions(sources);
  for (const cutter of canonicalizeRegions(cutters)) result = result.flatMap((source) => subtractRect(source, cutter));
  return canonicalizeRegions(result);
}

function intersectRegions(left: readonly Rect[], right: readonly Rect[]): Rect[] {
  return canonicalizeRegions(left.flatMap((a) => right.flatMap((b) => {
    const intersection = intersectRect(a, b);
    return intersection ? [intersection] : [];
  })));
}

export function combineRegions(
  selection: SelectionState,
  input: readonly Rect[],
  mode: SelectionCombineMode,
): SelectionState {
  const incoming = canonicalizeRegions(input);
  if (mode === 'replace') return { regions: incoming, inverted: false };
  const regions = canonicalizeRegions(selection.regions);
  if (!selection.inverted) {
    if (mode === 'add') return { regions: canonicalizeRegions([...regions, ...incoming]), inverted: false };
    if (mode === 'intersect') return { regions: intersectRegions(regions, incoming), inverted: false };
    return { regions: subtractRegions(regions, incoming), inverted: false };
  }
  if (mode === 'add') return { regions: subtractRegions(regions, incoming), inverted: true };
  if (mode === 'subtract') return { regions: canonicalizeRegions([...regions, ...incoming]), inverted: true };
  return { regions: subtractRegions(incoming, regions), inverted: false };
}

export function selectionRegionsWithin(selection: SelectionState, bounds: Rect): Rect[] {
  const canvas = normalizeRect(bounds);
  if (!hasSelection(selection)) return [canvas];
  return selection.inverted
    ? subtractRegions([canvas], selection.regions)
    : intersectRegions(selection.regions, [canvas]);
}

export function selectionBounds(selection: SelectionState, bounds: Rect): Rect | null {
  const regions = selectionRegionsWithin(selection, bounds);
  if (regions.length === 0) return null;
  const x = Math.min(...regions.map((rect) => rect.x));
  const y = Math.min(...regions.map((rect) => rect.y));
  const right = Math.max(...regions.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...regions.map((rect) => rect.y + rect.height));
  return { x, y, width: right - x, height: bottom - y };
}

export function combineRectangle(
  selection: SelectionState,
  input: Rect,
  mode: SelectionCombineMode,
): SelectionState {
  const rect = normalizeRect(input);
  if (rect.width === 0 || rect.height === 0) return selection;
  return combineRegions(selection, [rect], mode);
}
